import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PhoneCall, Loader2 } from "lucide-react";
import { usePhoneBar } from "@/contexts/PhoneBarContext";

interface Project {
  id: string;
  title: string;
  description: string;
  location: string;
  project_images: {
    image_url: string;
    alt_text: string;
    caption: string;
  }[];
}

// Static image mapping for projects without database images
const getStaticImageMapping = (title: string, serviceSlug?: string) => {
  const titleLower = title.toLowerCase();
  
  // Service-specific images
  if (serviceSlug === 'gutter-repair') {
    if (titleLower.includes('copper')) return "/images/projects/copper-gutter-installation.jpg";
    if (titleLower.includes('seamless')) return "/images/projects/seamless-gutter-system.jpg";
    return "/images/projects/gutter-installation.jpg";
  }
  
  if (serviceSlug === 'chimney-repair') {
    if (titleLower.includes('restoration')) return "/images/projects/chimney-restoration.jpg";
    if (titleLower.includes('masonry')) return "/images/projects/chimney-masonry-repair.jpg";
    return "/images/projects/chimney-repair.jpg";
  }
  
  // Roofing images
  if (titleLower.includes('residential') || titleLower.includes('bay shore')) {
    return "/images/projects/residential-roof-replacement.jpg";
  }
  if (titleLower.includes('commercial') || titleLower.includes('hempstead')) {
    return "/images/projects/commercial-roofing-project.jpg";
  }
  if (titleLower.includes('metal') || titleLower.includes('smithtown')) {
    return "/images/projects/metal-roofing-installation.jpg";
  }
  if (titleLower.includes('tile') || titleLower.includes('east hampton')) {
    return "/images/projects/tile-roof-restoration.jpg";
  }
  if (titleLower.includes('flat') || titleLower.includes('westbury')) {
    return "/images/projects/flat-roof-membrane-system.jpg";
  }
  if (titleLower.includes('shingle') || titleLower.includes('levittown')) {
    return "/images/projects/modern-shingle-installation.jpg";
  }
  // Default fallback
  return "/images/projects/residential-roof-replacement.jpg";
};

interface ProjectGalleryProps {
  serviceSlug?: string;
}

export const ProjectGallery = ({ serviceSlug }: ProjectGalleryProps = {}) => {
  const { phoneNumber } = usePhoneBar();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, [serviceSlug]);

  const loadProjects = async () => {
    try {
      // First, try to get page-specific gallery projects
      let pageQuery = supabase
        .from('page_gallery_projects')
        .select(`
          project_id,
          display_order,
          projects (
            id,
            title,
            description,
            location,
            project_images(image_url, alt_text, caption),
            project_category_assignments (
              category_id,
              project_categories(slug)
            )
          )
        `)
        .eq('is_active', true);

      // Filter by service if provided
      if (serviceSlug) {
        pageQuery = pageQuery.eq('page_type', 'service').eq('service_slug', serviceSlug);
      } else {
        pageQuery = pageQuery.eq('page_type', 'homepage');
      }

      const { data: pageGalleryData, error: pageGalleryError } = await pageQuery
        .order('display_order')
        .limit(6);

      let processedProjects: any[] = [];

      if (pageGalleryData && pageGalleryData.length > 0) {
        // Use page-specific gallery projects
        processedProjects = pageGalleryData
          .map(pg => pg.projects)
          .filter(Boolean)
          .map(project => ({
            ...project,
            project_images: project.project_images && project.project_images.length > 0 
              ? project.project_images
              : [{
                  image_url: getStaticImageMapping(project.title, serviceSlug),
                  alt_text: `${project.title} - Professional services by Safeway Roofing`,
                  caption: ""
                }]
          }));
      } else {
        // Fallback: Use automatic filtering by service categories
        // First, get featured project IDs to exclude
        const { data: featuredData } = await supabase
          .from('featured_projects')
          .select('project_id')
          .eq('is_active', true);
        
        const featuredProjectIds = featuredData?.map(fp => fp.project_id) || [];

        // Build query for projects
        let query = supabase
          .from('projects')
          .select(`
            id,
            title,
            description,
            location,
            project_images(image_url, alt_text, caption),
            project_category_assignments!inner(
              category_id,
              project_categories(slug)
            )
          `)
          .eq('status', 'published')
          .order('created_at', { ascending: false });

        // Filter by service if provided
        if (serviceSlug) {
          // Get category IDs for this service
          const { data: serviceCats } = await supabase
            .from('service_categories')
            .select(`
              category_id,
              services!inner(slug),
              project_categories(slug)
            `)
            .eq('services.slug', serviceSlug);

          if (serviceCats && serviceCats.length > 0) {
            const categoryIds = serviceCats.map(sc => sc.category_id);
            query = query.in('project_category_assignments.category_id', categoryIds);
          }
        }

        // Exclude featured projects if any exist
        if (featuredProjectIds.length > 0) {
          query = query.not('id', 'in', `(${featuredProjectIds.join(',')})`);
        }

        const { data, error } = await query;

        if (error) throw error;
        
        // Process all projects (with or without images)
        processedProjects = (data || []).map(project => ({
          ...project,
          project_images: project.project_images && project.project_images.length > 0 
            ? project.project_images
            : [{
                image_url: getStaticImageMapping(project.title, serviceSlug),
                alt_text: `${project.title} - Professional services by Safeway Roofing`,
                caption: ""
              }]
        }));
      }
      
      setProjects(processedProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
      
      // Service-specific fallback projects
      const fallbackProjects = serviceSlug === 'gutter-repair' ? [
        {
          id: '1',
          title: "Seamless Gutter Installation",
          description: "Professional seamless gutter system installation",
          location: "Long Island, NY",
          project_images: [{
            image_url: "/images/projects/gutter-installation.jpg",
            alt_text: "Professional seamless gutter installation by Safeway Roofing Long Island NY",
            caption: ""
          }]
        }
      ] : serviceSlug === 'chimney-repair' ? [
        {
          id: '1',
          title: "Chimney Masonry Repair",
          description: "Professional chimney repair and restoration services",
          location: "Long Island, NY",
          project_images: [{
            image_url: "/images/projects/chimney-repair.jpg",
            alt_text: "Professional chimney repair services by Safeway Roofing Long Island NY",
            caption: ""
          }]
        }
      ] : [
        {
          id: '1',
          title: "Modern Shingle Roof Installation",
          description: "Professional asphalt shingle roof installation",
          location: "Long Island, NY",
          project_images: [{
            image_url: "/images/projects/modern-shingle-installation.jpg",
            alt_text: "Professional asphalt shingle roof installation by Safeway Roofing Long Island NY",
            caption: ""
          }]
        },
        {
          id: '2',
          title: "Commercial Roofing Project",
          description: "Commercial roofing contractor flat roof installation",
          location: "Long Island, NY",
          project_images: [{
            image_url: "/images/projects/commercial-roofing-project.jpg",
            alt_text: "Commercial roofing contractor Long Island flat roof installation",
            caption: ""
          }]
        },
        {
          id: '3',
          title: "Tile Roof Restoration",
          description: "Professional tile roof restoration and repair services",
          location: "Long Island, NY",
          project_images: [{
            image_url: "/images/projects/tile-roof-restoration.jpg",
            alt_text: "Professional tile roof restoration and repair services Long Island",
            caption: ""
          }]
        }
      ];
      setProjects(fallbackProjects);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="gallery" className="py-20 bg-blue-50/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {serviceSlug === 'gutter-repair' 
              ? 'Our Gutter Installation Projects'
              : serviceSlug === 'chimney-repair'
              ? 'Our Chimney Repair Projects'
              : 'Our Recent Roofing Projects'
            }
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {serviceSlug === 'gutter-repair'
              ? 'Explore our completed gutter installation and repair projects across Long Island. From seamless gutter systems to copper gutters, see our expert craftsmanship.'
              : serviceSlug === 'chimney-repair'
              ? 'View our portfolio of chimney repair and restoration projects. Our expert masons deliver quality chimney services throughout Long Island.'
              : 'Browse our portfolio of completed roofing projects across Long Island. From residential roof replacements to commercial installations, see the quality craftsmanship that makes us the #1 choice for roofing contractors.'
            }
          </p>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden">
                <CardContent className="p-0">
                  <Skeleton className="w-full h-64" />
                  <div className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error && projects.length === 0 ? (
          <Alert className="mb-16">
            <AlertDescription>
              Unable to load projects at this time. Please try again later.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {projects.map((project) => (
              <Card key={project.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                <CardContent className="p-0">
                  <div className="relative overflow-hidden">
                    <img 
                      src={project.project_images[0]?.image_url} 
                      alt={project.project_images[0]?.alt_text || project.title}
                      className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <div className="text-white p-4">
                        <p className="font-semibold">{project.title}</p>
                        {project.location && (
                          <p className="text-sm opacity-90">{project.location}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16 px-8 rounded-2xl">
          <h3 className="text-3xl font-bold mb-4">
            {serviceSlug === 'gutter-repair'
              ? 'Ready to Install New Gutters?'
              : serviceSlug === 'chimney-repair'
              ? 'Need Chimney Repair Services?'
              : 'Ready to Start Your Roofing Project?'
            }
          </h3>
          <p className="text-xl mb-8 opacity-90">
            Get Your Free Estimate Today - Professional {serviceSlug === 'gutter-repair' ? 'Gutter' : serviceSlug === 'chimney-repair' ? 'Chimney' : 'Roofing'} Services Available 24/7
          </p>
          <Button
            size="lg"
            variant="secondary"
            className="bg-white text-blue-900 hover:bg-blue-50 font-bold px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            onClick={() => window.location.href = `tel:${phoneNumber}`}
            aria-label="Call Safeway Roofing for free estimate"
          >
            <PhoneCall className="w-5 h-5 mr-3" />
            Call {phoneNumber.replace('+1', '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')} Now
          </Button>
        </div>
      </div>
    </section>
  );
};